import { useEffect, useMemo, useState, type FormEvent } from "react";
import * as QRCode from "qrcode";
import {
  ArrowLeft,
  BadgeCheck,
  Building2,
  Check,
  ChevronRight,
  CircleAlert,
  Clipboard,
  Eye,
  EyeOff,
  KeyRound,
  LoaderCircle,
  LockKeyhole,
  Mail,
  Send,
  Search,
  ShieldCheck,
  Sparkles,
  UserPlus,
  UserRoundCheck,
} from "lucide-react";
import { ApiFailure, api } from "./api.js";
import type { Me } from "./auth-types.js";
import { BrandMark } from "./BrandMark.js";

interface InvitationPreview {
  legalName: string;
  maskedEmail: string;
  roles: string[];
  expiresAt: string;
}

interface Enrollment {
  accountId: string;
  subjectId: string;
  email: string;
  totpSecret: string;
  otpAuthUri: string;
}

interface RegistrationApplication {
  id: string;
  applicationNo: string;
  legalName: string;
  status: "SUBMITTED" | "UNDER_REVIEW" | "APPROVED" | "REJECTED";
  submittedAt: string;
  updatedAt: string;
  nextStep: string;
  requestId: string;
}

interface RegistrationDocument {
  version: string;
  digest: string;
  url: string;
}

interface RegistrationCapabilities {
  status: "AVAILABLE" | "UNAVAILABLE";
  channel: "EMAIL";
  purpose: "REGISTER";
  policy: { ttlSeconds: number; resendAfterSeconds: number; maxVerificationAttempts: number };
  accountTerms: RegistrationDocument | null;
  privacyPolicy: RegistrationDocument | null;
  unavailableReason?: string;
}

interface EmailChallenge {
  challengeId: string;
  maskedDestination: string;
  deliveryState: "PROVIDER_ACCEPTED";
  expiresAt: string;
  resendAvailableAt: string;
  attemptsRemaining: number;
}

interface RegisteredAccount {
  accountId: string;
  maskedEmail: string;
  contactVerification: "VERIFIED";
  identityVerification: "NOT_STARTED";
  createdAt: string;
}

type PortalMode = "login" | "register";
type LoginStep = "password" | "mfa" | "enrollment" | "recovery" | "reset-request" | "recovery-codes";
type RegisterRoute = "email" | "invite" | "application" | "status";
type InviteStep = "token" | "profile" | "enrollment";

function shortId(value: string, head = 8): string {
  return value.length > head * 2 + 1 ? `${value.slice(0, head)}…${value.slice(-head)}` : value;
}

function formatTime(value: string): string {
  return new Intl.DateTimeFormat("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

function roleName(role: string): string {
  const names: Record<string, string> = {
    PLATFORM_ADMIN: "平台管理员",
    ORG_ADMIN: "企业管理员",
    EVIDENCE_CREATOR: "存证经办人",
    EVIDENCE_VIEWER: "存证查看人",
    AUDITOR: "审计员",
  };
  return names[role] ?? role;
}

function inviteTokenFromLocation(): string {
  const value = new URL(window.location.href).searchParams.get("invite")?.trim() ?? "";
  return value.length >= 32 ? value : "";
}

function registrationDeviceId(): string {
  const key = "lmx.registration-device.v1";
  const created = crypto.randomUUID();
  try {
    const stored = localStorage.getItem(key)?.trim() ?? "";
    if (/^[A-Za-z0-9._:-]{16,128}$/u.test(stored)) return stored;
    localStorage.setItem(key, created);
  } catch {
    // Storage-restricted browsers still receive a random per-page identifier.
  }
  return created;
}

function ErrorNotice({ error }: { error: ApiFailure }): React.JSX.Element {
  return (
    <div className="error-notice" role="alert">
      <CircleAlert size={18} />
      <span><b>{error.message}</b><small>{error.code}{error.requestId ? ` · 请求 ${shortId(error.requestId, 6)}` : ""}</small></span>
    </div>
  );
}

function CopyButton({ value, label = "复制" }: { value: string; label?: string }): React.JSX.Element {
  const [copied, setCopied] = useState(false);
  const copy = async (): Promise<void> => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };
  return <button className="copy-button" type="button" onClick={() => void copy()}>{copied ? <Check /> : <Clipboard />}{copied ? "已复制" : label}</button>;
}

function PasswordField({
  value,
  onChange,
  autoComplete,
  label,
  autoFocus = false,
}: {
  value: string;
  onChange: (value: string) => void;
  autoComplete: string;
  label: string;
  autoFocus?: boolean;
}): React.JSX.Element {
  const [visible, setVisible] = useState(false);
  return (
    <label>{label}
      <span className="password-field">
        <input
          autoComplete={autoComplete}
          type={visible ? "text" : "password"}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          required
          autoFocus={autoFocus}
        />
        <button type="button" onClick={() => setVisible((current) => !current)} aria-label={visible ? "隐藏密码" : "显示密码"}>
          {visible ? <EyeOff /> : <Eye />}
        </button>
      </span>
    </label>
  );
}

function SecretField({
  value,
  onChange,
  label,
  placeholder,
  autoFocus = false,
}: {
  value: string;
  onChange: (value: string) => void;
  label: string;
  placeholder?: string;
  autoFocus?: boolean;
}): React.JSX.Element {
  const [visible, setVisible] = useState(false);
  return (
    <label>{label}
      <span className="password-field secret-field">
        <input
          className="token-input"
          autoComplete="off"
          type={visible ? "text" : "password"}
          value={value}
          onChange={(event) => onChange(event.target.value.trim())}
          placeholder={placeholder}
          minLength={32}
          maxLength={128}
          required
          autoFocus={autoFocus}
        />
        <button type="button" onClick={() => setVisible((current) => !current)} aria-label={visible ? `隐藏${label}` : `显示${label}`}>
          {visible ? <EyeOff /> : <Eye />}
        </button>
      </span>
    </label>
  );
}

function PasswordRules({ password, matches }: { password: string; matches: boolean }): React.JSX.Element {
  const rules = [
    [password.length >= 12, "至少 12 位"],
    [/[A-Z]/u.test(password) && /[a-z]/u.test(password), "包含大小写字母"],
    [/\d/u.test(password), "包含数字"],
    [/[^A-Za-z0-9]/u.test(password), "包含特殊字符"],
    [matches, "两次输入一致"],
  ] as const;
  return <div className="password-rules">{rules.map(([passed, text]) => <span className={passed ? "passed" : ""} key={text}><Check />{text}</span>)}</div>;
}

function EmailRegistration({ onGoToLogin }: { onGoToLogin: (email: string) => void }): React.JSX.Element {
  const [capabilities, setCapabilities] = useState<RegistrationCapabilities | null>(null);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [challenge, setChallenge] = useState<EmailChallenge | null>(null);
  const [account, setAccount] = useState<RegisteredAccount | null>(null);
  const [sendKey, setSendKey] = useState(() => crypto.randomUUID());
  const [accountKey, setAccountKey] = useState(() => crypto.randomUUID());
  const [deviceId] = useState(registrationDeviceId);
  const [now, setNow] = useState(Date.now());
  const [busy, setBusy] = useState<"send" | "create" | null>(null);
  const [error, setError] = useState<ApiFailure | null>(null);
  const passwordValid = password.length >= 12 && /[A-Z]/u.test(password) && /[a-z]/u.test(password) && /\d/u.test(password) && /[^A-Za-z0-9]/u.test(password);

  useEffect(() => {
    let active = true;
    void api<RegistrationCapabilities>("/v2/registration/capabilities")
      .then((response) => { if (active) setCapabilities(response.data); })
      .catch((cause) => {
        if (active) setError(cause instanceof ApiFailure ? cause : new ApiFailure(500, "UNKNOWN", "无法读取邮箱注册能力。"));
      });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!challenge) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, [challenge]);

  const resendSeconds = challenge
    ? Math.max(0, Math.ceil((new Date(challenge.resendAvailableAt).getTime() - now) / 1_000))
    : 0;
  const expired = challenge ? new Date(challenge.expiresAt).getTime() <= now : false;

  const updateEmail = (value: string): void => {
    setEmail(value);
    setChallenge(null);
    setAccount(null);
    setCode("");
    setSendKey(crypto.randomUUID());
    setAccountKey(crypto.randomUUID());
    setError(null);
  };

  const sendCode = async (): Promise<void> => {
    if (capabilities?.status !== "AVAILABLE" || busy || resendSeconds > 0) return;
    const idempotencyKey = challenge ? crypto.randomUUID() : sendKey;
    if (challenge) {
      setSendKey(idempotencyKey);
      setAccountKey(crypto.randomUUID());
      setCode("");
    }
    setBusy("send");
    setError(null);
    try {
      const response = await api<EmailChallenge>("/v2/contact-verifications", {
        method: "POST",
        headers: { "idempotency-key": idempotencyKey, "x-lmx-device-id": deviceId },
        body: JSON.stringify({ channel: "EMAIL", purpose: "REGISTER", destination: email }),
      });
      setChallenge(response.data);
      setNow(Date.now());
    } catch (cause) {
      setError(cause instanceof ApiFailure ? cause : new ApiFailure(500, "UNKNOWN", "验证码邮件发送失败。"));
    } finally {
      setBusy(null);
    }
  };

  const createAccount = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (!challenge || expired || !passwordValid || password !== confirmation || !acceptTerms || !acceptPrivacy) return;
    if (!capabilities?.accountTerms || !capabilities.privacyPolicy) return;
    setBusy("create");
    setError(null);
    try {
      const response = await api<RegisteredAccount>("/v2/accounts", {
        method: "POST",
        headers: { "idempotency-key": accountKey },
        body: JSON.stringify({
          challengeId: challenge.challengeId,
          code,
          password,
          acceptTerms,
          acceptPrivacy,
          accountTermsVersion: capabilities.accountTerms.version,
          privacyPolicyVersion: capabilities.privacyPolicy.version,
        }),
      });
      setAccount(response.data);
      setPassword("");
      setConfirmation("");
    } catch (cause) {
      const failure = cause instanceof ApiFailure ? cause : new ApiFailure(500, "UNKNOWN", "无法创建账号。");
      const attemptsRemaining = failure.details?.attemptsRemaining;
      if (typeof attemptsRemaining === "number" && Number.isInteger(attemptsRemaining)) {
        setChallenge((current) => current ? { ...current, attemptsRemaining } : current);
      }
      setError(failure);
    } finally {
      setBusy(null);
    }
  };

  if (account) {
    return (
      <section className="auth-form account-created-panel">
        <span className="completion-icon"><BadgeCheck /></span>
        <span className="form-kicker">邮箱已验证 · 全局账号已创建</span>
        <h2>下一步完成实名认证</h2>
        <p className="form-intro">账号创建和验证码消费已在同一笔 MySQL 事务中完成。你可以继续选择个人认证或加入、创建企业认证关系。</p>
        <div className="account-created-summary">
          <Mail />
          <span><b>{account.maskedEmail}</b><small>联系方式：已验证 · 实名状态：未开始</small></span>
        </div>
        <div className="receipt-item"><code>{account.accountId}</code><CopyButton value={account.accountId} label="复制账号 ID" /><small>全局账号 ID · 创建于 {formatTime(account.createdAt)}</small></div>
        <div className="success-notice"><ShieldCheck /><span><b>现在可以完成首次安全登录</b><small>请使用刚设置的密码登录，然后绑定 TOTP 动态验证器；会话只在二次验证成功后签发。</small></span></div>
        <button className="primary-button" type="button" onClick={() => onGoToLogin(email)}><KeyRound />去首次安全登录</button>
      </section>
    );
  }

  return (
    <form className="auth-form email-registration-form" onSubmit={(event) => void createAccount(event)}>
      <span className="form-kicker">邮箱开户注册</span>
      <h2>创建链脉全局账号</h2>
      <p className="form-intro">先验证邮箱并设置密码。账号创建后仍需单独完成个人或企业实名认证，注册成功不代表身份已核验。</p>
      {!capabilities && !error && <div className="resume-notice"><LoaderCircle className="spin" /><span><b>正在读取注册能力</b><small>确认 MySQL 身份库和邮件适配器均可用后才允许发送。</small></span></div>}
      {capabilities?.status === "UNAVAILABLE" && <div className="warning-notice"><CircleAlert /><span><b>邮箱注册暂不可用</b><small>服务端能力未启用（{capabilities.unavailableReason ?? "UNKNOWN"}），发送按钮保持关闭。</small></span></div>}
      <label>邮箱地址
        <span className="verification-field">
          <input autoComplete="email" type="email" placeholder="name@company.cn" value={email} onChange={(event) => updateEmail(event.target.value)} required autoFocus />
          <button type="button" onClick={() => void sendCode()} disabled={capabilities?.status !== "AVAILABLE" || !email || busy !== null || resendSeconds > 0}>
            {busy === "send" ? <LoaderCircle className="spin" /> : <Send />}
            {busy === "send" ? "投递中" : resendSeconds > 0 ? `${resendSeconds} 秒` : challenge ? "重新发送" : "发送验证码"}
          </button>
        </span>
      </label>
      {challenge && <div className={expired ? "warning-notice challenge-notice" : "success-notice challenge-notice"}><Mail /><span><b>{expired ? "验证码已过期" : `邮件服务商已受理，正在投递至 ${challenge.maskedDestination}`}</b><small>{expired ? "请重新发送验证码。" : `请以真实收件箱为准。5 分钟内有效，最多校验 ${challenge.attemptsRemaining} 次；${resendSeconds > 0 ? `${resendSeconds} 秒后可重发。` : "现在可以重发。"}`}</small></span></div>}
      <label>6 位邮箱验证码<input className="otp-input" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} value={code} onChange={(event) => setCode(event.target.value.replace(/\D/gu, ""))} disabled={!challenge || expired} required /></label>
      <PasswordField label="设置登录密码" autoComplete="new-password" value={password} onChange={setPassword} />
      <PasswordField label="再次输入密码" autoComplete="new-password" value={confirmation} onChange={setConfirmation} />
      <PasswordRules password={password} matches={confirmation.length > 0 && password === confirmation} />
      <label className="check-row policy-row"><input type="checkbox" checked={acceptTerms} onChange={(event) => setAcceptTerms(event.target.checked)} required /><span>我已阅读并同意 {capabilities?.accountTerms ? <a href={capabilities.accountTerms.url} target="_blank" rel="noreferrer">《用户协议》</a> : "《用户协议》"}（版本 {capabilities?.accountTerms?.version ?? "未配置"}）。</span></label>
      <label className="check-row policy-row"><input type="checkbox" checked={acceptPrivacy} onChange={(event) => setAcceptPrivacy(event.target.checked)} required /><span>我已阅读并同意 {capabilities?.privacyPolicy ? <a href={capabilities.privacyPolicy.url} target="_blank" rel="noreferrer">《隐私政策》</a> : "《隐私政策》"}（版本 {capabilities?.privacyPolicy?.version ?? "未配置"}）。</span></label>
      {error && <ErrorNotice error={error} />}
      <button className="primary-button" disabled={busy !== null || capabilities?.status !== "AVAILABLE" || !challenge || challenge.attemptsRemaining < 1 || expired || code.length !== 6 || !passwordValid || password !== confirmation || !acceptTerms || !acceptPrivacy}>{busy === "create" ? <LoaderCircle className="spin" /> : <UserPlus />}验证并创建账号</button>
    </form>
  );
}

function LoginForm({ onAuthenticated, initialEmail, onRegister }: {
  onAuthenticated: (me: Me) => void;
  initialEmail: string;
  onRegister: () => void;
}): React.JSX.Element {
  const [step, setStep] = useState<LoginStep>("password");
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [challengeId, setChallengeId] = useState("");
  const [challengeExpiresAt, setChallengeExpiresAt] = useState("");
  const [challengeStore, setChallengeStore] = useState<"MYSQL" | "LEGACY_SQLITE">("MYSQL");
  const [enrollment, setEnrollment] = useState<{ secret: string; otpAuthUri: string } | null>(null);
  const [enrollmentQr, setEnrollmentQr] = useState("");
  const [deviceId] = useState(registrationDeviceId);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiFailure | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [recoveryCode, setRecoveryCode] = useState("");
  const [resetReason, setResetReason] = useState("");
  const [newRecoveryCodes, setNewRecoveryCodes] = useState<string[]>([]);
  const [pendingMe, setPendingMe] = useState<Me | null>(null);

  useEffect(() => { if (initialEmail) setEmail(initialEmail); }, [initialEmail]);

  useEffect(() => {
    if (!enrollment) {
      setEnrollmentQr("");
      return;
    }
    let active = true;
    void QRCode.toDataURL(enrollment.otpAuthUri, { width: 188, margin: 1, errorCorrectionLevel: "M" })
      .then((dataUrl) => { if (active) setEnrollmentQr(dataUrl); })
      .catch(() => { if (active) setEnrollmentQr(""); });
    return () => { active = false; };
  }, [enrollment]);

  const submitPassword = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const response = await api<{
        identityStore: "MYSQL" | "LEGACY_SQLITE";
        challengeStore?: "LEGACY_SQLITE";
        status: "MFA_REQUIRED" | "TOTP_ENROLLMENT_REQUIRED";
        challengeId: string;
        expiresAt: string;
        enrollment?: { secret: string; otpAuthUri: string };
      }>("/v2/auth/sessions", {
        method: "POST",
        headers: { "x-lmx-device-id": deviceId },
        body: JSON.stringify({ email, password }),
      });
      setChallengeId(response.data.challengeId);
      setChallengeExpiresAt(response.data.expiresAt);
      setChallengeStore(response.data.identityStore === "LEGACY_SQLITE" ? "LEGACY_SQLITE" : "MYSQL");
      setEnrollment(response.data.enrollment ?? null);
      setPassword("");
      setStep(response.data.status === "TOTP_ENROLLMENT_REQUIRED" ? "enrollment" : "mfa");
    } catch (cause) {
      setError(cause instanceof ApiFailure ? cause : new ApiFailure(500, "UNKNOWN", "无法连接身份服务。"));
    } finally {
      setBusy(false);
    }
  };

  const submitMfa = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const response = await api<Me>(`/v2/auth/challenges/${challengeId}/totp/verify`, {
        method: "POST",
        headers: { "x-lmx-device-id": deviceId },
        body: JSON.stringify({ code, challengeStore }),
      });
      const recoveryCodes = (response.meta as { recoveryCodes?: string[] } | undefined)?.recoveryCodes ?? [];
      if (recoveryCodes.length > 0) {
        setPendingMe(response.data);
        setNewRecoveryCodes(recoveryCodes);
        setStep("recovery-codes");
      } else {
        onAuthenticated(response.data);
      }
    } catch (cause) {
      setError(cause instanceof ApiFailure ? cause : new ApiFailure(500, "UNKNOWN", "无法连接身份服务。"));
    } finally {
      setBusy(false);
    }
  };

  const submitRecoveryCode = async (event: FormEvent): Promise<void> => {
    event.preventDefault(); setBusy(true); setError(null);
    try {
      const response = await api<{
        status: "TOTP_ENROLLMENT_REQUIRED"; challengeId: string; expiresAt: string;
        enrollment: { secret: string; otpAuthUri: string };
      }>(`/v2/auth/challenges/${challengeId}/recovery-code/verify`, {
        method: "POST", headers: { "x-lmx-device-id": deviceId }, body: JSON.stringify({ recoveryCode }),
      });
      setChallengeId(response.data.challengeId); setChallengeExpiresAt(response.data.expiresAt);
      setEnrollment(response.data.enrollment); setRecoveryCode(""); setCode(""); setStep("enrollment");
    } catch (cause) { setError(cause instanceof ApiFailure ? cause : new ApiFailure(500, "UNKNOWN", "无法使用恢复码。")); }
    finally { setBusy(false); }
  };

  const submitResetRequest = async (event: FormEvent): Promise<void> => {
    event.preventDefault(); setBusy(true); setError(null);
    try {
      await api(`/v2/auth/challenges/${challengeId}/mfa-reset-requests`, {
        method: "POST", headers: { "x-lmx-device-id": deviceId, "idempotency-key": crypto.randomUUID() },
        body: JSON.stringify({ reason: resetReason }),
      });
      setResetReason(""); setStep("password");
      setNotice("高风险账号恢复申请已提交。完成身份复核、两名安全管理员批准、真实通知和 24 小时冷静期前，旧 MFA 不会被重置。");
    } catch (cause) { setError(cause instanceof ApiFailure ? cause : new ApiFailure(500, "UNKNOWN", "无法提交账号恢复申请。")); }
    finally { setBusy(false); }
  };

  if (step === "password") return (
    <form className="auth-form" onSubmit={(event) => void submitPassword(event)}>
      <span className="form-kicker">可信身份登录</span>
      <h2>欢迎回到链脉信枢</h2>
      <p className="form-intro">使用企业已开户注册的邮箱和密码登录，密码通过后仍需完成动态验证码校验。</p>
      <label>登录邮箱<input autoComplete="username" type="email" placeholder="name@company.cn" value={email} onChange={(event) => setEmail(event.target.value)} required autoFocus /></label>
      <PasswordField label="登录密码" autoComplete="current-password" value={password} onChange={setPassword} />
      {notice && <div className="success-notice" role="status"><ShieldCheck /><span><b>恢复申请已受理</b><small>{notice}</small></span></div>}
      {error && <ErrorNotice error={error} />}
      <button className="primary-button" disabled={busy}>{busy ? <LoaderCircle className="spin" /> : <KeyRound />}验证密码并继续</button>
      <div className="auth-help"><span>尚未开户注册？</span><button type="button" onClick={onRegister}>使用邮箱注册</button></div>
    </form>
  );

  if (step === "recovery-codes" && pendingMe) return (
    <section className="auth-form">
      <span className="form-kicker">MFA 恢复完成</span><h2>立即保存新的恢复码</h2>
      <p className="form-intro">旧 TOTP、旧恢复码和其他会话已经失效。以下 10 个恢复码只显示一次，每个只能使用一次。</p>
      <div className="recovery-code-grid">{newRecoveryCodes.map((item) => <code key={item}>{item}</code>)}</div>
      <CopyButton value={newRecoveryCodes.join("\n")} label="复制全部恢复码" />
      <div className="warning-notice"><CircleAlert /><span><b>请离线保存</b><small>不要保存到聊天记录或与登录密码放在同一位置。</small></span></div>
      <button className="primary-button" type="button" onClick={() => onAuthenticated(pendingMe)}><ShieldCheck />我已安全保存，进入工作台</button>
    </section>
  );

  if (step === "recovery") return (
    <form className="auth-form" onSubmit={(event) => void submitRecoveryCode(event)}>
      <button className="auth-back" type="button" onClick={() => { setStep("mfa"); setError(null); }}><ArrowLeft />返回动态验证</button>
      <span className="form-kicker">一次性恢复</span><h2>使用恢复码</h2>
      <p className="form-intro">密码已经通过。恢复码验证成功后，旧 TOTP、旧恢复码和其他会话会在同一事务中失效，并要求立即绑定新的验证器。</p>
      <label>恢复码<input className="mono-input" autoComplete="one-time-code" value={recoveryCode} onChange={(event) => setRecoveryCode(event.target.value.toUpperCase())} placeholder="LMX-XXXXX-XXXXX-XXXXX-XXXXX" required autoFocus /></label>
      {error && <ErrorNotice error={error} />}
      <button className="primary-button" disabled={busy || recoveryCode.replace(/[\s-]/gu, "").length !== 23}>{busy ? <LoaderCircle className="spin" /> : <KeyRound />}消费恢复码并更换 MFA</button>
      <div className="auth-help"><span>恢复码也遗失？</span><button type="button" onClick={() => { setStep("reset-request"); setError(null); }}>申请人工受控重置</button></div>
    </form>
  );

  if (step === "reset-request") return (
    <form className="auth-form" onSubmit={(event) => void submitResetRequest(event)}>
      <button className="auth-back" type="button" onClick={() => { setStep("mfa"); setError(null); }}><ArrowLeft />返回动态验证</button>
      <span className="form-kicker">高风险账号恢复</span><h2>申请人工重置 MFA</h2>
      <p className="form-intro">该流程不会立即关闭 MFA。必须经过独立身份复核、两名安全管理员、真实通知送达门禁和 24 小时安全冷静期。</p>
      <label>无法使用 MFA 的原因<textarea minLength={10} maxLength={2000} value={resetReason} onChange={(event) => setResetReason(event.target.value)} required autoFocus /></label>
      {error && <ErrorNotice error={error} />}
      <button className="primary-button" disabled={busy || resetReason.trim().length < 10}>{busy ? <LoaderCircle className="spin" /> : <ShieldCheck />}提交受控恢复申请</button>
    </form>
  );

  return (
    <form className="auth-form" onSubmit={(event) => void submitMfa(event)}>
      <button className="auth-back" type="button" onClick={() => { setStep("password"); setCode(""); setEnrollment(null); setError(null); }}><ArrowLeft />返回密码登录</button>
      <span className="form-kicker">{step === "enrollment" ? "首次登录安全设置" : "第二重验证"}</span>
      <h2>{step === "enrollment" ? "绑定动态验证器" : "输入动态验证码"}</h2>
      {step === "enrollment" && enrollment ? (
        <>
          <p className="form-intro">密码已经通过。请先用任意兼容 TOTP 的验证器扫描二维码，再输入当前 6 位验证码完成绑定；绑定成功前不会创建登录会话。</p>
          <div className="enrollment-box login-enrollment-box">
            <div className="qr-box">{enrollmentQr ? <img src={enrollmentQr} alt="链脉信枢首次登录 TOTP 二维码" /> : <LoaderCircle className="spin" />}</div>
            <div><small>无法扫码时手动输入</small><code>{enrollment.secret}</code><CopyButton value={enrollment.secret} label="复制密钥" /></div>
          </div>
          <div className="warning-notice"><CircleAlert /><span><b>这是账户第二重验证密钥</b><small>请勿截图发送给他人。系统只有在验证码验证成功后才会激活该因子。</small></span></div>
        </>
      ) : (
        <p className="form-intro">请输入验证器中“链脉信枢”当前的 6 位验证码。本次挑战将于 {challengeExpiresAt ? formatTime(challengeExpiresAt) : "5 分钟后"} 失效。</p>
      )}
      <label>6 位动态验证码<input className="otp-input" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} value={code} onChange={(event) => setCode(event.target.value.replace(/\D/gu, ""))} required autoFocus /></label>
      {error && <ErrorNotice error={error} />}
      <button className="primary-button" disabled={busy || code.length !== 6}>{busy ? <LoaderCircle className="spin" /> : <ShieldCheck />}{step === "enrollment" ? "验证、激活并登录" : "验证并进入工作台"}</button>
      {step === "mfa" && challengeStore === "MYSQL" && <div className="auth-help"><span>无法使用验证器？</span><button type="button" onClick={() => { setStep("recovery"); setError(null); }}>使用恢复码</button></div>}
    </form>
  );
}

function InviteRegistration({ initialToken, onGoToLogin }: { initialToken: string; onGoToLogin: (email?: string) => void }): React.JSX.Element {
  const [step, setStep] = useState<InviteStep>("token");
  const [token, setToken] = useState(initialToken);
  const [preview, setPreview] = useState<InvitationPreview | null>(null);
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [enrollment, setEnrollment] = useState<Enrollment | null>(null);
  const [qrCode, setQrCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiFailure | null>(null);
  const passwordValid = password.length >= 12 && /[A-Z]/u.test(password) && /[a-z]/u.test(password) && /\d/u.test(password) && /[^A-Za-z0-9]/u.test(password);

  useEffect(() => {
    if (!enrollment) return;
    let active = true;
    void QRCode.toDataURL(enrollment.otpAuthUri, { width: 188, margin: 1, errorCorrectionLevel: "M" })
      .then((dataUrl) => { if (active) setQrCode(dataUrl); })
      .catch(() => { if (active) setQrCode(""); });
    return () => { active = false; };
  }, [enrollment]);

  const inspect = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const response = await api<InvitationPreview>("/v1/invitations/inspect", {
        method: "POST",
        body: JSON.stringify({ token }),
      });
      setPreview(response.data);
      setStep("profile");
    } catch (cause) {
      setError(cause instanceof ApiFailure ? cause : new ApiFailure(500, "UNKNOWN", "无法校验企业邀请。"));
    } finally {
      setBusy(false);
    }
  };

  const register = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (!passwordValid || password !== confirmation || !confirmed) return;
    setBusy(true);
    setError(null);
    try {
      const response = await api<Enrollment>("/v1/invitations/accept", {
        method: "POST",
        body: JSON.stringify({ token, displayName, password }),
      });
      setEnrollment(response.data);
      setPassword("");
      setConfirmation("");
      setStep("enrollment");
    } catch (cause) {
      setError(cause instanceof ApiFailure ? cause : new ApiFailure(500, "UNKNOWN", "无法完成开户注册。"));
    } finally {
      setBusy(false);
    }
  };

  if (step === "enrollment" && enrollment) {
    return (
      <section className="auth-form enrollment-panel">
        <span className="completion-icon"><BadgeCheck /></span>
        <span className="form-kicker">账户已创建 · 还需绑定 MFA</span>
        <h2>保存你的动态验证密钥</h2>
        <p className="form-intro">这是唯一一次展示绑定材料。请用验证器扫描二维码或手动输入密钥，保存完成后再进入登录。</p>
        <div className="enrollment-box">
          <div className="qr-box">{qrCode ? <img src={qrCode} alt="链脉信枢 TOTP 绑定二维码" /> : <LoaderCircle className="spin" />}</div>
          <div><small>手动绑定密钥</small><code>{enrollment.totpSecret}</code><CopyButton value={enrollment.totpSecret} label="复制密钥" /></div>
        </div>
        <div className="warning-notice"><CircleAlert /><span><b>密钥只在当前页面显示</b><small>关闭或刷新页面后，系统不会通过普通接口再次返回。请勿截图转发给他人。</small></span></div>
        <button className="primary-button" type="button" onClick={() => onGoToLogin(enrollment.email)}><ShieldCheck />我已保存，去登录验证</button>
      </section>
    );
  }

  if (step === "profile" && preview) {
    return (
      <form className="auth-form" onSubmit={(event) => void register(event)}>
        <button className="auth-back" type="button" onClick={() => { setStep("token"); setPreview(null); setError(null); }}><ArrowLeft />更换邀请码</button>
        <span className="form-kicker">受邀开户注册</span>
        <h2>设置账户安全信息</h2>
        <div className="invitation-summary"><Building2 /><span><b>{preview.legalName}</b><small>{preview.maskedEmail} · 有效期至 {formatTime(preview.expiresAt)}</small></span></div>
        <div className="role-chips">{preview.roles.map((role) => <span key={role}>{roleName(role)}</span>)}</div>
        <label>姓名<input autoComplete="name" value={displayName} onChange={(event) => setDisplayName(event.target.value)} minLength={2} maxLength={80} required /></label>
        <PasswordField label="设置登录密码" autoComplete="new-password" value={password} onChange={setPassword} />
        <PasswordField label="再次输入密码" autoComplete="new-password" value={confirmation} onChange={setConfirmation} />
        <PasswordRules password={password} matches={confirmation.length > 0 && password === confirmation} />
        <label className="check-row"><input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} required /><span>我确认本人属于上述企业，并将妥善保管密码和动态验证密钥。</span></label>
        {error && <ErrorNotice error={error} />}
        <button className="primary-button" disabled={busy || !passwordValid || password !== confirmation || !confirmed}>{busy ? <LoaderCircle className="spin" /> : <UserPlus />}创建账户并绑定 MFA</button>
      </form>
    );
  }

  return (
    <form className="auth-form" onSubmit={(event) => void inspect(event)}>
      <span className="form-kicker">受邀开户注册</span>
      <h2>校验企业邀请</h2>
      <p className="form-intro">链脉信枢不开放匿名账户自动成为可信身份。请输入企业管理员发送的一次性邀请凭证。</p>
      <SecretField label="企业邀请码" value={token} onChange={setToken} placeholder="粘贴一次性邀请凭证" autoFocus />
      {error && <ErrorNotice error={error} />}
      <button className="primary-button" disabled={busy || token.length < 32}>{busy ? <LoaderCircle className="spin" /> : <BadgeCheck />}校验邀请并继续</button>
      <div className="security-note compact-note"><LockKeyhole /><span>邀请凭证只用于确认企业、邮箱和角色；校验不会创建账户，也不会消耗邀请。</span></div>
    </form>
  );
}

function EnterpriseApplication(): React.JSX.Element {
  const [form, setForm] = useState({ legalName: "", creditCode: "", applicantName: "", email: "", mobile: "", intendedUsage: "" });
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiFailure | null>(null);
  const [receipt, setReceipt] = useState<{ application: RegistrationApplication; token: string } | null>(null);
  const [idempotencyKey] = useState(() => crypto.randomUUID());

  const update = (field: keyof typeof form, value: string): void => setForm((current) => ({ ...current, [field]: value }));
  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (!confirmed) return;
    setBusy(true);
    setError(null);
    try {
      const response = await api<RegistrationApplication>("/v1/registration-applications", {
        method: "POST",
        headers: { "idempotency-key": idempotencyKey },
        body: JSON.stringify({
          legalName: form.legalName,
          unifiedSocialCreditCode: form.creditCode,
          applicantName: form.applicantName,
          email: form.email,
          mobile: form.mobile,
          intendedUsage: form.intendedUsage,
        }),
      });
      if (!response.statusCredential?.token) throw new ApiFailure(500, "STATUS_CREDENTIAL_MISSING", "申请已受理，但查询凭证未返回。请联系平台并提供请求编号。");
      setReceipt({ application: response.data, token: response.statusCredential.token });
    } catch (cause) {
      setError(cause instanceof ApiFailure ? cause : new ApiFailure(500, "UNKNOWN", "无法提交企业入驻申请。"));
    } finally {
      setBusy(false);
    }
  };

  if (receipt) {
    return (
      <section className="auth-form application-receipt">
        <span className="completion-icon"><Check /></span>
        <span className="form-kicker">入驻申请已受理</span>
        <h2>请保存申请查询凭证</h2>
        <p className="form-intro">当前状态仅表示资料已落库，不代表企业身份已核验，也没有创建登录账户。</p>
        <div className="receipt-item"><small>申请编号</small><code>{receipt.application.applicationNo}</code><CopyButton value={receipt.application.applicationNo} /></div>
        <div className="receipt-item"><small>查询凭证</small><code>{receipt.token}</code><CopyButton value={receipt.token} /></div>
        <div className="application-state"><i /><span><b>待平台核验</b><small>{receipt.application.nextStep}</small></span></div>
        <div className="warning-notice"><CircleAlert /><span><b>查询凭证只在本次回执中展示</b><small>请和申请编号一起妥善保存，平台不会把明文查询凭证写入数据库。</small></span></div>
      </section>
    );
  }

  return (
    <form className="auth-form application-form" onSubmit={(event) => void submit(event)}>
      <span className="form-kicker">企业入驻申请</span>
      <h2>申请开通企业空间</h2>
      <p className="form-intro">提交后由平台核验企业主体。审核通过后才会签发管理员邀请，本步骤不会直接创建账户。</p>
      <div className="field-grid">
        <label className="field-wide">企业法定名称<input value={form.legalName} onChange={(event) => update("legalName", event.target.value)} minLength={2} maxLength={120} required autoFocus /></label>
        <label className="field-wide">统一社会信用代码<input className="mono-input" value={form.creditCode} onChange={(event) => update("creditCode", event.target.value.toUpperCase().replace(/\s/gu, ""))} minLength={18} maxLength={18} pattern="[0-9A-HJ-NPQRTUWXY]{18}" required /></label>
        <label>申请人姓名<input autoComplete="name" value={form.applicantName} onChange={(event) => update("applicantName", event.target.value)} minLength={2} maxLength={80} required /></label>
        <label>中国大陆手机号<input autoComplete="tel" inputMode="numeric" value={form.mobile} onChange={(event) => update("mobile", event.target.value.replace(/\D/gu, ""))} pattern="1[3-9][0-9]{9}" maxLength={11} required /></label>
        <label className="field-wide">企业工作邮箱<input autoComplete="email" type="email" value={form.email} onChange={(event) => update("email", event.target.value)} required /></label>
        <label className="field-wide">拟使用场景<textarea value={form.intendedUsage} onChange={(event) => update("intendedUsage", event.target.value)} minLength={10} maxLength={500} rows={3} placeholder="例如：合同文件可信存证、跨机构验证和内部审计" required /></label>
      </div>
      <label className="check-row"><input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} required /><span>我确认所填信息真实有效，并授权平台仅为企业身份核验和开户注册目的处理这些资料。</span></label>
      {error && <ErrorNotice error={error} />}
      <button className="primary-button" disabled={busy || !confirmed}>{busy ? <LoaderCircle className="spin" /> : <Building2 />}提交入驻申请</button>
    </form>
  );
}

function ApplicationStatus(): React.JSX.Element {
  const [applicationNo, setApplicationNo] = useState("");
  const [statusToken, setStatusToken] = useState("");
  const [result, setResult] = useState<RegistrationApplication | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiFailure | null>(null);
  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const response = await api<RegistrationApplication>("/v1/registration-applications/status", {
        method: "POST",
        body: JSON.stringify({ applicationNo, statusToken }),
      });
      setResult(response.data);
    } catch (cause) {
      setResult(null);
      setError(cause instanceof ApiFailure ? cause : new ApiFailure(500, "UNKNOWN", "无法查询入驻申请。"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="auth-form" onSubmit={(event) => void submit(event)}>
      <span className="form-kicker">申请进度查询</span>
      <h2>查看企业入驻状态</h2>
      <p className="form-intro">申请编号和查询凭证必须同时正确。查询凭证不会出现在地址栏或服务日志中。</p>
      <label>申请编号<input className="mono-input" value={applicationNo} onChange={(event) => setApplicationNo(event.target.value.toUpperCase().trim())} placeholder="LMR-YYYYMMDD-XXXXXXXX" required autoFocus /></label>
      <SecretField label="查询凭证" value={statusToken} onChange={setStatusToken} />
      {error && <ErrorNotice error={error} />}
      {result && <div className="status-result"><span className={`status-dot status-${result.status.toLowerCase()}`} /><span><b>{result.legalName}</b><small>{result.applicationNo} · 更新于 {formatTime(result.updatedAt)}</small><p>{result.nextStep}</p></span></div>}
      <button className="primary-button" disabled={busy || statusToken.length < 32}>{busy ? <LoaderCircle className="spin" /> : <Search />}查询申请状态</button>
    </form>
  );
}

export function AuthPortal({ onAuthenticated }: { onAuthenticated: (me: Me) => void }): React.JSX.Element {
  const initialInviteToken = useMemo(inviteTokenFromLocation, []);
  const [mode, setMode] = useState<PortalMode>(initialInviteToken ? "register" : "login");
  const [registerRoute, setRegisterRoute] = useState<RegisterRoute>(initialInviteToken ? "invite" : "email");
  const [loginEmail, setLoginEmail] = useState("");

  useEffect(() => {
    if (!initialInviteToken) return;
    const url = new URL(window.location.href);
    url.searchParams.delete("invite");
    window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
  }, [initialInviteToken]);

  const goToLogin = (email = ""): void => {
    setLoginEmail(email);
    setMode("login");
  };

  return (
    <main className="login-shell">
      <section className="login-story">
        <div className="brand brand-on-dark"><BrandMark /><span><b>链脉信枢</b><small>LIANMAI TRUST HUB</small></span></div>
        <div className="story-copy">
          <span className="eyebrow"><Sparkles size={15} /> 可信身份入口</span>
          <h1>身份可信，<br /><em>业务才可信。</em></h1>
          <p>先验证邮箱建立全局账号，后续可绑定手机号，再选择个人认证或关联多个企业，身份核验通过后进入可信存证工作台。</p>
        </div>
        <div className="trust-route" aria-label="可信身份路径">
          <div><Mail /><span>联系方式验证<small>邮箱 Challenge · 5 分钟有效</small></span></div>
          <ChevronRight />
          <div><UserRoundCheck /><span>全局账号<small>个人唯一 · 企业可多绑</small></span></div>
          <ChevronRight />
          <div><Building2 /><span>实名认证<small>个人认证 / 企业认证</small></span></div>
        </div>
        <div className="identity-principle"><LockKeyhole /><span><b>不以“注册成功”替代“身份已核验”</b><small>联系方式验证、全局账号、个人认证和企业认证是独立状态，并保留审计记录。</small></span></div>
        <footer>lianmai.net.cn · 京ICP备2026024994号-1</footer>
      </section>

      <section className="login-panel">
        <div className={`login-card ${mode === "register" && (registerRoute === "application" || registerRoute === "email") ? "wide-card" : ""}`}>
          <div className="mobile-brand brand"><BrandMark /><span><b>链脉信枢</b><small>LIANMAI TRUST HUB</small></span></div>
          <div className="auth-tabs" role="tablist" aria-label="账户入口">
            <button className={mode === "login" ? "active" : ""} type="button" role="tab" aria-selected={mode === "login"} onClick={() => setMode("login")}><KeyRound />登录</button>
            <button className={mode === "register" ? "active" : ""} type="button" role="tab" aria-selected={mode === "register"} onClick={() => setMode("register")}><UserPlus />注册</button>
          </div>

          {mode === "login" ? (
            <LoginForm onAuthenticated={onAuthenticated} initialEmail={loginEmail} onRegister={() => { setMode("register"); setRegisterRoute("email"); }} />
          ) : (
            <>
              <div className="register-routes">
                <button className={registerRoute === "email" ? "active" : ""} type="button" onClick={() => setRegisterRoute("email")}>邮箱注册</button>
                <button className={registerRoute === "invite" ? "active" : ""} type="button" onClick={() => setRegisterRoute("invite")}>受邀注册</button>
                <button className={registerRoute === "application" ? "active" : ""} type="button" onClick={() => setRegisterRoute("application")}>企业入驻</button>
                <button className={registerRoute === "status" ? "active" : ""} type="button" onClick={() => setRegisterRoute("status")}>查询申请</button>
              </div>
              {registerRoute === "email" && <EmailRegistration onGoToLogin={goToLogin} />}
              {registerRoute === "invite" && <InviteRegistration initialToken={initialInviteToken} onGoToLogin={goToLogin} />}
              {registerRoute === "application" && <EnterpriseApplication />}
              {registerRoute === "status" && <ApplicationStatus />}
              <div className="auth-help"><span>已经有账户？</span><button type="button" onClick={() => goToLogin()}>返回登录</button></div>
            </>
          )}
          <div className="security-note"><LockKeyhole size={16} /><span>登录会话使用 HttpOnly、SameSite Cookie；密码采用独立随机盐的 scrypt 哈希，动态验证密钥加密保存。</span></div>
        </div>
      </section>
    </main>
  );
}
